import type { ReactNode } from 'react';
import {
  DollarOutlined,
  LineChartOutlined,
  MailOutlined,
  SettingOutlined,
  TeamOutlined,
} from '@ant-design/icons';

// Тип ссылки живёт вместе с компонентом, который её рисует и фильтрует правом:
// два объявления одной формы разъехались бы, и `permissions` можно было бы
// «забыть» на этой стороне, не сломав сборку.
import type { MigrationPlaceholderLink } from '@shared/components/MigrationPlaceholder';

export interface MigrationPlaceholderRoute {
  /** Путь без ведущего слэша — как объявляются вложенные маршруты в router.tsx */
  path: string;
  /** Ключ заголовка, тот же, что у пункта меню */
  titleKey: string;
  icon: ReactNode;
  /**
   * Ключ описания: чего именно нет в V5. Задаётся там, где общего «раздел не
   * перенесён» оператору мало, чтобы понять, чего он лишился и куда идти.
   */
  descriptionKey?: string;
  /** Ключ пояснения, на какой вопрос отвечает соседний экран с частью данных */
  relatedNoteKey?: string;
  /**
   * Соседние экраны с частью данных — список, а не одна ссылка: раздел v4 мог
   * распасться в V5 на несколько (см. «Склады» ниже).
   */
  related?: ReadonlyArray<MigrationPlaceholderLink>;
}

/**
 * Реестр разделов, на которые ведут пункты меню (и карточки хаба настроек),
 * но экранов для которых в V5 пока нет.
 *
 * Заглушка ставится ТОЛЬКО если действия нет нигде в V5. Если функция уже
 * доступна на другом экране — пункт меню ведут туда, а не сюда: заглушка над
 * работающей функцией врёт оператору сильнее, чем битая ссылка.
 *
 * НАЛИЧИЕ ПРОВЕРЯЕТСЯ ПОВЕДЕНИЕМ ЭКРАНА ЭТАЛОНА, А НЕ ПОДПИСЬЮ ПУНКТА. Совпадение
 * слова — не совпадение сущности, и это уже давало три ложных вывода подряд
 * (аудит 2026-08-14): «Оборудование» в настройках v4 — браузер по ВНЕШНЕЙ базе
 * вендора, а не каталог моделей SNMP; «Склад» — места хранения с правами
 * доступа, а не номенклатура; «Табель» — сводная сетка подразделение × месяц, а
 * не вкладка в карточке. Перед тем как снять заглушку — открыть эталонный класс
 * и посмотреть, что он делает.
 *
 * Частичное наличие — случай не «есть» и не «нет», а ТРЕТИЙ: описание называет,
 * чего именно нет, а `related` ведёт на то, что есть. Врать в обе стороны нельзя:
 * ни «раздел не перенесён», когда часть доехала, ни увести пункт на соседний
 * экран, выдав его за перенесённый раздел.
 */
export const MIGRATION_PLACEHOLDER_ROUTES: MigrationPlaceholderRoute[] = [
  // Абоненты — графики. Не хватает именно ВРЕМЕННОГО РЯДА: «как менялось со
  // временем». Текущий срез («как сейчас») уже есть в блоке аналитики списка
  // абонентов, поэтому заглушка ниже ссылается туда.
  //
  // customers/charts/activity ЗАГЛУШКОЙ БОЛЬШЕ НЕ ЯВЛЯЕТСЯ (growth-charts.1, 2026-09-01) —
  // реальная страница CustomerActivityChartPage зарегистрирована выше по router.tsx.
  // customers/charts/growth ЗАГЛУШКОЙ БОЛЬШЕ НЕ ЯВЛЯЕТСЯ (growth-charts.2, 2026-09-02) —
  // реальная страница CustomerGrowthChartPage зарегистрирована в router.tsx под гейтом
  // customers.analytics.growth (эталон CustomerChartIncreaseHtml.php).

  // Абоненты — отчёты без аналога в V5.
  //
  // Заглушки `customers/activity-internet` здесь БОЛЬШЕ НЕТ: интернет-метрика
  // выведена на живой экран `/customers/activity` переключателем источника, и
  // пункт меню ведёт туда через ключ-псевдоним (shared/config/menuAlias.ts).
  //
  // Как и в v4, это два пресета ОДНОГО списка, отличающиеся колонкой даты:
  // CustomerListHtml::internetActivity() (CustomerListHtml.php:1104) фильтрует по
  // F_DATE_ACTIVITY_INET (`date_last_activity_internet`, метрика со стороны
  // биллинга), CustomerListHtml::netActivity() (:1109) — по F_DATE_ACTIVITY
  // (`date_last_activity_net`, со стороны сети). Отдельного отчёта нет ни там, ни
  // там. Подмены одного измерения другим не происходит: источник выбирается явно,
  // а у абонентов без источника показывается «нет данных», а не «офлайн».
  //
  // «Метки» заглушкой БОЛЬШЕ НЕ закрыты: v4-раскладка списка «с меткой / без метки»
  // (CustomerListHtml::A_TAGS, CustomerListHtml.php:989-1066) доехала живым экраном
  // /customers/tags под правом customers.mark.read (в v4 — право 33, BodyHtml.php:1045).
  // Справочник меток — по-прежнему отдельный экран, /settings/object-marks.
  { path: 'customers/billing-status', titleKey: 'menu:customers_billing_status', icon: <DollarOutlined /> },

  // Тарифы заглушками НЕ закрыты вовсе: список, группы, услуги и «Трафик».
  // «Трафик» — агрегат потребления (erp.customers.traffic_in|traffic_out через
  // erp.customers_tariffs, эталон TariffHtml.php:71-137) — доехал живым экраном
  // /tariffs/traffic под правом customers.view, как и остальной тарифный раздел.

  // Сообщения: «Входящие», «Исходящие» и «Архив» заглушкой БОЛЬШЕ НЕ закрыты — все три
  // мигрированы одним движком под правом customers.messages (эталон MessageHtml.php:
  // :1491 inbox, :1383 outbox, :1444 archive).
  // «Общесетевые» — не рассылка, а CRUD объявлений для личного кабинета абонента
  // (отдельная таблица erp.public_messages, которой в V5 нет вовсе).
  { path: 'messages/broadcast', titleKey: 'menu:messages_broadcast', icon: <MailOutlined /> },

  // Персонал — «Подразделения» заглушкой БОЛЬШЕ НЕ закрыты: дерево на чтение
  // смонтировано (router.tsx, гейт employee.view). Записи в нём нет сознательно —
  // см. docs/specs/2026-08-14-divisions-tree-readonly.md.
  //
  // «Табель» v4 (EmployeeHtml::A_TIMESHEET, EmployeeHtml.php:955-1170) — сводная
  // сетка ПОДРАЗДЕЛЕНИЕ × МЕСЯЦ: строки-сотрудники, колонки-дни, три типа времени
  // ($arrTabel[date][employee_id][time_type]), перелистывание месяцев и печать
  // (ApiInternal::staff_timesheet_print_header). Такой сводки в V5 нет.
  // Табель ОДНОГО сотрудника доехал — вкладкой в его карточке
  // (UsersDetailPage.tsx, useEmployeeTimesheet(employeeId)); это ровно то, что в
  // v4 открывалось из ячейки сетки. Своего адреса у вкладки нет (нужен id), поэтому
  // ссылка ведёт на список, откуда открывается карточка.
  {
    path: 'employees/timesheet',
    titleKey: 'menu:employees_timesheet',
    icon: <TeamOutlined />,
    descriptionKey: 'common:migration_placeholder.employees_timesheet.description',
    relatedNoteKey: 'common:migration_placeholder.employees_timesheet.related',
    related: [{ path: '/users', labelKey: 'menu:users_list', permissions: ['employee.view'] }],
  },

  // Настройки — на эти пути ведут и пункты меню, и карточки хаба настроек
  //
  // `settings/equipment` снят с заглушки: это SettingsMainHtml::ITEM_7, а не
  // SettingsEquipBaseHtml. Первый живой срез — общий цвет радиосектора.
  { path: 'settings/notifications', titleKey: 'menu:settings_notifications', icon: <SettingOutlined /> },
  // `settings/documents` из реестра заглушек снят 2026-08-14: редактор шаблонов печатных
  // документов построен (SettingsDocumentsPage), маршрут смонтирован в router.tsx,
  // гейт — settings.documents.manage.
  //
  // `settings/warehouse` из реестра заглушек снят 2026-08-24: CRUD складов построен
  // (WarehouseSettingsPage — имя/комментарий, доступ пер-сотрудник read/write, главный склад,
  // уведомление о транзитных ТМЦ), маршрут /warehouse/settings смонтирован в router.tsx,
  // гейт — inventory.catalog.manage.
];
